import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
  Stack,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress,
  Checkbox,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import {
  AutoMode,
  ExpandMore,
  Assignment,
  Person,
  Schedule,
  CheckCircle,
  Warning,
  Settings,
  Preview,
  PlayArrow,
  SelectAll,
  Deselect,
  FilterList
} from '@mui/icons-material';
import { API_URL } from '../utils/apiConfig';

const FILL_METHODS = {
  'optimal': {
    label: 'Optimal',
    description: 'Best overall score considering all factors'
  },
  'greedy': {
    label: 'Greedy',
    description: 'Always pick the highest-scored candidate'
  },
  'balanced': {
    label: 'Balanced',
    description: 'Distribute hours evenly among employees'
  }
};

function AssignmentPreview({ assignments, failed = [], selectedAssignments, onSelectionChange }) {
  if (assignments.length === 0 && failed.length === 0) {
    return (
      <Alert severity="info">
        No assignments to preview. Try adjusting your settings.
      </Alert>
    );
  }

  const handleSelectAll = () => {
    const allIds = new Set(assignments.map(a => a.shiftId));
    onSelectionChange(allIds);
  };

  const handleDeselectAll = () => {
    onSelectionChange(new Set());
  };

  const handleSelectPreferred = () => {
    const preferredIds = new Set(
      assignments.filter(a => a.preference === 'preferred').map(a => a.shiftId)
    );
    onSelectionChange(preferredIds);
  };

  const handleToggle = (shiftId) => {
    const newSelection = new Set(selectedAssignments);
    if (newSelection.has(shiftId)) {
      newSelection.delete(shiftId);
    } else {
      newSelection.add(shiftId);
    }
    onSelectionChange(newSelection);
  };

  const isAllSelected = assignments.length > 0 && selectedAssignments.size === assignments.length;
  const isPartiallySelected = selectedAssignments.size > 0 && selectedAssignments.size < assignments.length;

  return (
    <Box>
      {assignments.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                Successful Assignments ({assignments.length})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${selectedAssignments.size} of ${assignments.length} selected`}
                  color={selectedAssignments.size > 0 ? 'primary' : 'default'}
                  size="small"
                />
                <ButtonGroup size="small" variant="outlined">
                  <Tooltip title="Select All">
                    <Button onClick={handleSelectAll} startIcon={<SelectAll />}>
                      All
                    </Button>
                  </Tooltip>
                  <Tooltip title="Select Only Preferred">
                    <Button onClick={handleSelectPreferred} startIcon={<FilterList />}>
                      Preferred
                    </Button>
                  </Tooltip>
                  <Tooltip title="Deselect All">
                    <Button onClick={handleDeselectAll} startIcon={<Deselect />}>
                      None
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              </Stack>
            </Stack>
            <TableContainer sx={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartiallySelected}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                      />
                    </TableCell>
                    {assignments.some(a => a.location) && <TableCell>Location</TableCell>}
                    <TableCell>Employee</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Preference</TableCell>
                    <TableCell>Hours</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment, index) => (
                    <TableRow
                      key={index}
                      hover
                      onClick={() => handleToggle(assignment.shiftId)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAssignments.has(assignment.shiftId)}
                          onChange={() => handleToggle(assignment.shiftId)}
                        />
                      </TableCell>
                      {assignments.some(a => a.location) && (
                        <TableCell>
                          <Chip
                            label={assignment.location || 'Unknown'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell>{assignment.employeeName}</TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assignment.date || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assignment.timeRange || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assignment.role || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={assignment.preference}
                          size="small"
                          color={
                            assignment.preference === 'preferred' ? 'success' :
                            assignment.preference === 'available' ? 'primary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {assignment.currentHours}h → {assignment.newTotalHours.toFixed(1)}h
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {failed.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              Failed Assignments ({failed.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These shifts could not be auto-assigned. Consider adjusting availability or settings.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default function AutoScheduleDialog({
  open,
  onClose,
  scheduleId,
  schedules = [], // Array of schedules for multi-location mode
  locations = [], // Location details
  onComplete,
  scopeDate // optional YYYY-MM-DD to limit to a day
}) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('settings'); // 'settings', 'preview', 'complete'
  const [selectedAssignments, setSelectedAssignments] = useState(new Set());
  const [multiLocationMode, setMultiLocationMode] = useState(false);

  // Configuration state
  const [config, setConfig] = useState({
    respectPreferences: true,
    maxHoursPerEmployee: 40,
    prioritizeFullTime: true,
    fillMethod: 'optimal',
    allowOvertime: false,
    maxOvertimeHours: 8,
    ignoreRoles: false,
    ignoreAvailability: false
  });

  // Auto-detect multi-location mode when dialog opens
  useEffect(() => {
    if (open) {
      setMultiLocationMode(schedules.length > 1);
    }
  }, [open, schedules]);

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handlePreview = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');

      if (multiLocationMode && schedules.length > 1) {
        // Multi-location mode: run preview for each schedule
        const allPreviewData = [];

        for (const schedule of schedules) {
          const params = new URLSearchParams({
            scheduleId: schedule.id,
            options: JSON.stringify(scopeDate ? { ...config, date: scopeDate } : config)
          });

          const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/auto-schedule/preview?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const data = await response.json();

          if (response.ok) {
            const location = locations.find(l => l.id === schedule.location_id);
            const weekStart = new Date(schedule.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const locationName = location?.name || `Location ${schedule.location_id}`;
            const label = schedules.some(s => s.id !== schedule.id && s.location_id === schedule.location_id)
              ? `${locationName} (${weekStart})`  // Show week if multiple weeks for same location
              : locationName;  // Otherwise just location name

            allPreviewData.push({
              schedule,
              location: label,
              locationId: schedule.location_id,
              ...data
            });
          }
        }

        // Combine all previews
        const combined = {
          byLocation: allPreviewData,
          suggestions: allPreviewData.flatMap(d => d.suggestions.map(s => ({ ...s, locationId: d.locationId, location: d.location }))),
          stats: {
            totalOpenShifts: allPreviewData.reduce((sum, d) => sum + d.stats.totalOpenShifts, 0),
            unassignable: allPreviewData.reduce((sum, d) => sum + d.stats.unassignable, 0)
          },
          config: scopeDate ? { ...config, date: scopeDate } : config
        };

        setPreview(combined);
        // Auto-select all assignments by default
        const allIds = new Set(combined.suggestions.map(s => s.shiftId));
        setSelectedAssignments(allIds);
      } else {
        // Single location mode
        const params = new URLSearchParams({
          scheduleId,
          options: JSON.stringify(scopeDate ? { ...config, date: scopeDate } : config)
        });

        const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/auto-schedule/preview?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to preview auto-schedule');
        }

        setPreview(data);
        // Auto-select all assignments by default
        const allIds = new Set(data.suggestions.map(s => s.shiftId));
        setSelectedAssignments(allIds);
      }

      setStep('preview');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');

      if (multiLocationMode && preview.byLocation) {
        // Multi-location mode: execute for each location with selected assignments
        const results = [];

        for (const locationData of preview.byLocation) {
          // Filter selected assignments for this location
          const locationAssignments = Array.from(selectedAssignments).filter(shiftId =>
            preview.suggestions.some(s => s.shiftId === shiftId && s.locationId === locationData.locationId)
          );

          if (locationAssignments.length > 0) {
            const selectedOptions = {
              ...(scopeDate ? { ...config, date: scopeDate } : config),
              assignmentIds: locationAssignments
            };

            const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/auto-schedule`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                scheduleId: locationData.schedule.id,
                options: selectedOptions
              })
            });

            const data = await response.json();

            if (response.ok) {
              results.push({
                location: locationData.location,
                ...data
              });
            }
          }
        }

        setStep('complete');
        if (onComplete) {
          onComplete({ multiLocation: true, results });
        }
      } else {
        // Single location mode
        const selectedOptions = {
          ...(scopeDate ? { ...config, date: scopeDate } : config),
          assignmentIds: Array.from(selectedAssignments)
        };

        const response = await fetch(`${API_URL.replace(/\/$/, '')}/vnext/auto-schedule`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            scheduleId,
            options: selectedOptions
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to execute auto-schedule');
        }

        setStep('complete');
        if (onComplete) {
          onComplete(data);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('settings');
    setPreview(null);
    setError('');
    setSelectedAssignments(new Set());
    onClose();
  };

  const renderSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Settings />
        Auto-Schedule Settings
      </Typography>

      <Stack spacing={3}>
        <FormControl fullWidth>
          <InputLabel>Fill Method</InputLabel>
          <Select
            value={config.fillMethod}
            onChange={(e) => handleConfigChange('fillMethod', e.target.value)}
            label="Fill Method"
          >
            {Object.entries(FILL_METHODS).map(([key, method]) => (
              <MenuItem key={key} value={key}>
                <Box>
                  <Typography variant="body2">{method.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {method.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box>
          <TextField
            label="Max Hours Per Employee"
            type="number"
            value={config.maxHoursPerEmployee}
            onChange={(e) => handleConfigChange('maxHoursPerEmployee', parseInt(e.target.value))}
            inputProps={{ min: 1, max: 60 }}
            fullWidth
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={config.respectPreferences}
              onChange={(e) => handleConfigChange('respectPreferences', e.target.checked)}
            />
          }
          label="Respect Employee Preferences"
        />

        <FormControlLabel
          control={
            <Switch
              checked={config.prioritizeFullTime}
              onChange={(e) => handleConfigChange('prioritizeFullTime', e.target.checked)}
            />
          }
          label="Prioritize Full-Time Hours"
        />

        <FormControlLabel
          control={
            <Switch
              checked={config.allowOvertime}
              onChange={(e) => handleConfigChange('allowOvertime', e.target.checked)}
            />
          }
          label="Allow Overtime"
        />

        {config.allowOvertime && (
          <Box sx={{ ml: 4 }}>
            <TextField
              label="Max Overtime Hours"
              type="number"
              value={config.maxOvertimeHours}
              onChange={(e) => handleConfigChange('maxOvertimeHours', parseInt(e.target.value))}
              inputProps={{ min: 0, max: 20 }}
              fullWidth
            />
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={config.ignoreRoles}
              onChange={(e) => handleConfigChange('ignoreRoles', e.target.checked)}
            />
          }
          label="Ignore Role Requirements"
        />

        <FormControlLabel
          control={
            <Switch
              checked={config.ignoreAvailability}
              onChange={(e) => handleConfigChange('ignoreAvailability', e.target.checked)}
            />
          }
          label="Ignore Availability Windows (assign regardless of time preferences)"
        />
      </Stack>
    </Box>
  );

  const renderPreview = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Preview />
        Preview Results
      </Typography>

      {preview && (
        <Box>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Chip
                  label={`${preview.suggestions.length} Assignable`}
                  color="success"
                  icon={<CheckCircle />}
                />
                <Chip
                  label={`${preview.stats.unassignable} Unassignable`}
                  color={preview.stats.unassignable > 0 ? 'warning' : 'default'}
                  icon={<Warning />}
                />
                <Chip
                  label={`${preview.stats.totalOpenShifts} Total Open`}
                  color="primary"
                  icon={<Schedule />}
                />
              </Stack>

              {/* Multi-schedule breakdown */}
              {preview.byLocation && preview.byLocation.length > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Breakdown by Schedule:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {preview.byLocation.map((loc, idx) => (
                      <Chip
                        key={idx}
                        label={`${loc.location}: ${loc.suggestions.length} assignable`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Diagnostics from API to aid troubleshooting */}
              {preview.diagnostics && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Diagnostics
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`Employees: ${preview.diagnostics.employees ?? 0}`} />
                    <Chip size="small" label={`Availability records: ${preview.diagnostics.availability ?? 0}`} />
                    <Chip size="small" label={`Assigned shifts this week: ${preview.diagnostics.existingAssignedShifts ?? 0}`} />
                  </Stack>

                  {/* Helpful tips when nothing is assignable */}
                  {preview.suggestions.length === 0 && (
                    <Box sx={{ mt: 1 }}>
                      {preview.stats.totalOpenShifts === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          No open shifts found. Create open shifts or unassign existing ones to preview assignments.
                        </Typography>
                      )}
                      {preview.diagnostics.employees === 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          No active employees found for this schedule's location.
                        </Typography>
                      )}
                      {preview.diagnostics.availability === 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          No availability records found. Add employee availability to enable auto-scheduling.
                        </Typography>
                      )}
                      {preview.stats.totalOpenShifts > 0 && preview.diagnostics.employees > 0 && preview.diagnostics.availability > 0 && preview.diagnostics.unassignableReasons && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Why shifts can't be assigned:
                          </Typography>
                          {preview.diagnostics.unassignableReasons.role_mismatch > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              • Role mismatches: {preview.diagnostics.unassignableReasons.role_mismatch} (shift requires role employee doesn't have)
                            </Typography>
                          )}
                          {preview.diagnostics.unassignableReasons.no_availability > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              • No availability: {preview.diagnostics.unassignableReasons.no_availability} (employee not available during shift time)
                            </Typography>
                          )}
                          {preview.diagnostics.unassignableReasons.max_hours_exceeded > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              • Max hours: {preview.diagnostics.unassignableReasons.max_hours_exceeded} (would exceed {preview.config.maxHoursPerEmployee}h limit)
                            </Typography>
                          )}
                          {preview.diagnostics.unassignableReasons.overtime_not_allowed > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              • Overtime: {preview.diagnostics.unassignableReasons.overtime_not_allowed} (would require overtime but not allowed)
                            </Typography>
                          )}
                          {preview.diagnostics.unassignableReasons.time_conflict > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              • Time conflicts: {preview.diagnostics.unassignableReasons.time_conflict} (conflicts with existing assigned shifts)
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          <AssignmentPreview
            assignments={preview.suggestions}
            failed={[]} // Preview doesn't show failed, just unassignable count
            selectedAssignments={selectedAssignments}
            onSelectionChange={setSelectedAssignments}
          />

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>Configuration Used</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Fill Method:</strong> {FILL_METHODS[preview.config.fillMethod]?.label}
                </Typography>
                <Typography variant="body2">
                  <strong>Max Hours:</strong> {preview.config.maxHoursPerEmployee}
                </Typography>
                <Typography variant="body2">
                  <strong>Respect Preferences:</strong> {preview.config.respectPreferences ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  <strong>Prioritize Full-Time:</strong> {preview.config.prioritizeFullTime ? 'Yes' : 'No'}
                </Typography>
                <Typography variant="body2">
                  <strong>Allow Overtime:</strong> {preview.config.allowOvertime ? 'Yes' : 'No'}
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );

  const renderComplete = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Auto-Schedule Complete!
      </Typography>
      <Typography variant="body1" color="text.secondary">
        The schedule has been automatically filled based on your settings.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 500 }
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoMode />
          <Typography variant="h6">Auto-Schedule Shifts</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {step === 'preview' ? 'Generating preview...' : 'Executing auto-schedule...'}
            </Typography>
          </Box>
        )}

        {step === 'settings' && renderSettings()}
        {step === 'preview' && renderPreview()}
        {step === 'complete' && renderComplete()}
      </DialogContent>

      <DialogActions>
        {step === 'settings' && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={loading}
              startIcon={<Preview />}
            >
              Preview
            </Button>
          </>
        )}

        {step === 'preview' && (
          <>
            <Button onClick={() => setStep('settings')} disabled={loading}>
              Back to Settings
            </Button>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExecute}
              disabled={loading || selectedAssignments.size === 0}
              startIcon={<PlayArrow />}
              color="success"
            >
              Execute Selected ({selectedAssignments.size})
            </Button>
          </>
        )}

        {step === 'complete' && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
